import { useState, useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Spinner } from 'reactstrap'
import { Decimal } from 'decimal.js'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import ScreenContainer from 'components/ScreenContainer'

import { getGoalAmount, getGoalReward } from 'models/Rewards/selectors'
import { CRYPTO_CATEGORY } from 'models/Rewards/constants'
import { getCanUserRedeem, getParsedToken, getIsTokenValidFormat, getCryptoOptions } from 'models/Redemptions/selectors'
import { CRYPTO_REDEMPTION_SCREEN_KEYS } from 'models/Redemptions/constants'
import redemptionsActions from 'models/Redemptions/actions'
import { getBalance } from 'models/ProfileInfo/selectors'

import { EVENT_TYPE, STRING } from 'resources'
import { APP_URLS } from 'resources/urls'

import { intercomShowNewMessage } from 'services/Intercom'

import { getCryptoValue } from 'utils/format-crypto'
import { convertAmountToPoints, convertPointsToAmount } from 'utils/format-points'
import { sendAnalyticsEvent } from 'services/analytics'

import PointsConversionScreen from './CryptoDetail/PointsConversionScreen'
import CryptoExpirationRateBar from './CryptoExpirationRateBar'
import WalletAddressEnteringScreen from './CryptoDetail/WalletAddressEnteringScreen'
import ConfirmationScreen from './CryptoDetail/ConfirmationScreen'

import { CRYPTO_CONFIGS } from './constants'

import { SpinnerContainer, ScreenTitle, ContentContainer } from './styles'

import CryptoQuoteExpiredModal from './CryptoQuoteExpiredModal'

const recaptchaAction = 'web_crypto_redemption'

const CryptoRedemption = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const parsedToken = useSelector(getParsedToken)
  const canUserRedeem = useSelector(getCanUserRedeem)
  const cryptoOptions = useSelector(getCryptoOptions)
  const goalReward = useSelector(getGoalReward)
  const goalAmount = useSelector(getGoalAmount)

  const userBalance = useSelector(getBalance)
  const isTokenValidFormat = useSelector(getIsTokenValidFormat)

  const crypto = useMemo(() => location.state?.cryptoKey || 'bitcoin', [location])
  const userBalancePoints = useMemo(() => convertAmountToPoints(userBalance), [userBalance])
  const goalPoints = useMemo(() => convertAmountToPoints(goalAmount), [goalAmount])
  const minimumPoints = useMemo(
    () => convertAmountToPoints(cryptoOptions?.[crypto]?.minimumAmount || 0),
    [cryptoOptions, crypto],
  )

  const [currentScreenKey, setCurrentScreenKey] = useState(CRYPTO_REDEMPTION_SCREEN_KEYS.pointsConversionScreen)
  const [points, setPoints] = useState(goalPoints.toNumber())

  const [walletAddress, setWalletAddress] = useState('')
  const [hasTimeExpired, setHasTimeExpired] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isModalSuccessRedemptionOpen, setIsModalSuccessRedemptionOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const { currency, quoteCryptoPerAmount, estimatedNetworkFeeCrypto } = useMemo(
    () => ({
      currency: parsedToken?.currency ?? null,
      quoteCryptoPerAmount: getCryptoValue(parsedToken?.quoteCryptoPerAmount ?? 0, crypto),
      estimatedNetworkFeeCrypto: getCryptoValue(parsedToken?.estimatedNetworkFeeCrypto ?? 0, crypto),
    }),
    [parsedToken, crypto],
  )

  const pointsInCrypto = useMemo(
    () => getCryptoValue(convertPointsToAmount(points).times(quoteCryptoPerAmount), crypto),
    [points, quoteCryptoPerAmount, crypto],
  )
  const approximateValue = useMemo(
    () => (quoteCryptoPerAmount.eq(0) ? new Decimal(0) : new Decimal(pointsInCrypto).div(quoteCryptoPerAmount)),
    [pointsInCrypto, quoteCryptoPerAmount],
  )

  useEffect(() => {
    if (crypto) {
      setIsLoading(true)
      dispatch(redemptionsActions.requestCryptoRedemptionQuote.init(crypto, () => setIsLoading(false)))
    }
  }, [dispatch, crypto])

  useEffect(() => {
    if (
      !canUserRedeem ||
      !(crypto in CRYPTO_CONFIGS) ||
      (!isLoading && !isTokenValidFormat) ||
      goalReward.categoryId !== CRYPTO_CATEGORY
    ) {
      history.replace(APP_URLS.WALLET)
    }
  }, [canUserRedeem, crypto, goalReward, isLoading, isTokenValidFormat, history])

  useEffect(
    () => () => {
      dispatch(redemptionsActions.clearCryptoRedemptionQuote.success())
    },
    [dispatch],
  )

  const closeExpiredModal = () => {
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoRateExpiredModalRestartClick, { crypto })
    history.replace(APP_URLS.WALLET)
  }

  const requestCryptoRedemption = useCallback(
    (recaptchaToken) => {
      setIsSubmitting(true)
      setErrorMessage('')
      sendAnalyticsEvent(EVENT_TYPE.redeemCryptoConfirmationScreenRequestClick, { crypto })
      // eslint-disable-next-line no-console
      console.info(`[Recaptcha] Recaptcha verified for action=${recaptchaAction}, recaptchaToken=${recaptchaToken}`)
      const amount = convertPointsToAmount(points).toFixed(2)
      dispatch(
        redemptionsActions.requestCryptoRedemption.init(
          amount,
          walletAddress,
          recaptchaToken,
          (isSuccess, errorResponse) => {
            setIsSubmitting(false)
            if (!isSuccess) {
              if (errorResponse?.code === 'RecaptchaVerificationException') {
                sendAnalyticsEvent(EVENT_TYPE.captchaFail, { screenName: 'Redemption', score: errorResponse?.score })
                setErrorMessage(STRING.wallet.redemptions.recaptchaErrorMessage)
                intercomShowNewMessage(STRING.wallet.redemptions.recaptchaErrorSupport('redemption'))
              } else {
                setErrorMessage(STRING.cryptoRedeemScreen.confirmationScreen.redemptionRequestFailed)
              }
              return
            }

            setIsModalSuccessRedemptionOpen(true)
          },
        ),
      )
    },
    [crypto, dispatch, points, walletAddress],
  )

  const handleRequestCryptoRedemption = useCallback(async () => {
    try {
      const recaptchaToken = await executeRecaptcha(recaptchaAction)
      requestCryptoRedemption(recaptchaToken)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[Recaptcha] Recaptcha error for action=${recaptchaAction}`, error)
      setErrorMessage(STRING.wallet.redemptions.recaptchaErrorMessage)
      intercomShowNewMessage(STRING.wallet.redemptions.recaptchaErrorSupport('redemption'))
    }
  }, [executeRecaptcha, requestCryptoRedemption])

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }

  let currentScreen = null

  switch (currentScreenKey) {
    case CRYPTO_REDEMPTION_SCREEN_KEYS.pointsConversionScreen:
      currentScreen = (
        <>
          <ScreenTitle>
            {STRING.cryptoRedeemScreen.pointsConversionScreen.screenTitle(STRING.crypto[crypto].label).toUpperCase()}
          </ScreenTitle>
          <ScreenContainer>
            <ContentContainer>
              <PointsConversionScreen
                goToNextScreen={setCurrentScreenKey}
                crypto={crypto}
                currency={currency}
                points={points}
                setPoints={setPoints}
                pointsInCrypto={pointsInCrypto}
                approximateValue={approximateValue}
                maxPoints={userBalancePoints}
                minPoints={minimumPoints}
              />
            </ContentContainer>
          </ScreenContainer>
        </>
      )
      break
    case CRYPTO_REDEMPTION_SCREEN_KEYS.walletAddressEnteringScreen:
      currentScreen = (
        <>
          <ScreenTitle>{STRING.cryptoRedeemScreen.walletEnteringScreen.screenTitle}</ScreenTitle>
          <ScreenContainer>
            <ContentContainer>
              <WalletAddressEnteringScreen
                goToNextScreen={setCurrentScreenKey}
                crypto={crypto}
                walletAddress={walletAddress}
                setWalletAddress={setWalletAddress}
              />
            </ContentContainer>
          </ScreenContainer>
        </>
      )
      break
    case CRYPTO_REDEMPTION_SCREEN_KEYS.confirmationScreen:
      currentScreen = (
        <>
          <ScreenTitle>{STRING.cryptoRedeemScreen.confirmationScreen.screenTitle}</ScreenTitle>
          <ScreenContainer>
            <ContentContainer>
              <ConfirmationScreen
                crypto={crypto}
                walletAddress={walletAddress}
                points={points}
                pointsInCrypto={pointsInCrypto}
                estimatedNetworkFeeCrypto={estimatedNetworkFeeCrypto}
                requestCryptoRedemption={handleRequestCryptoRedemption}
                isModalSuccessRedemptionOpen={isModalSuccessRedemptionOpen}
                setIsModalSuccessRedemptionOpen={setIsModalSuccessRedemptionOpen}
                isSubmitting={isSubmitting}
                errorMessage={errorMessage}
                approximateValue={approximateValue}
                currency={currency}
              />
            </ContentContainer>
          </ScreenContainer>
        </>
      )
      break
    default:
      currentScreen = null
  }

  return (
    <>
      {!isModalSuccessRedemptionOpen && (
        <CryptoQuoteExpiredModal isOpen={hasTimeExpired} closeExpiredModal={closeExpiredModal} crypto={crypto} />
      )}
      <CryptoExpirationRateBar setHasTimeExpired={setHasTimeExpired} />
      {currentScreen}
    </>
  )
}

export default CryptoRedemption
